/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {findLastIndex} from 'lodash';
import {setRel} from '@ion/core/src/services/ConnectUtils';
import {
  AccountLink,
  Asset,
  Device,
  Entity,
  GenericObject,
  GroupMembers
} from '@ion/core/src/types/entities';
import {HierLevel} from '@ion/core/src/types/services';
// Defines the Group Actions
export enum GroupActions {
  Add = 'add',
  AddNew = 'addNew',
  Remove = 'remove',
  Replace = 'replace',
  ReplaceNew = 'replaceNew'
};

export enum GroupActionStatus {
  Hold = 'hold',
  Start = 'start',
  InProgress = 'inProgress',
  Success = 'success',
  Failed = 'failed'
};

export const getGroupMember = (entity, membersList, accountId) => {
  const members = {searchCriteria: []};
  const assetSearch = {memberSearch: {type: '', searchTerms: {}, hierLevel: HierLevel.ALL}};
  const deviceSearch = {smartSearch: {type: '', queryString: '', accountId: '', hierLevel: HierLevel.ALL}};
  if (entity === Entity.device) {
    deviceSearch.smartSearch.type = entity;
    deviceSearch.smartSearch.queryString = `id:(${membersList.join(' OR ')})`;
    deviceSearch.smartSearch.accountId = accountId;
    members.searchCriteria.push(deviceSearch);
  } else {
    assetSearch.memberSearch.type = entity;
    assetSearch.memberSearch.searchTerms = {
      id      : membersList.join(','),
      account : accountId
    };
    members.searchCriteria.push(assetSearch);
  }
  return members;
};

export const flattenGroupsList = (deviceList: GenericObject[]) => {
  let list = [];
  deviceList?.forEach?.(device => {
    if (device?.groups) {
      list = list.concat(device.groups);
    }
  });
  return list;
};

export const getUniqGroups = (items: GenericObject[]) => {
  return items?.reduce((unique, attr) => {
    if (!unique.some(({href}) => href === attr.href)) {
      unique.push(attr);
    }
    return unique;
  }, []);
};

export const checkGroupNameExists = (allGroups, groupName) => {
  let exist = false;
  if (groupName !== '') {
    const arr = allGroups?.filter((group) => {
      return group?.name?.toLocaleLowerCase() === groupName.toLocaleLowerCase();
    });
    exist = arr?.length > 0;
  }
  return exist;
};

export const validateActionExists = (actionArray, groupName, accountName) => {
  const lowerGroupName = groupName?.toLocaleLowerCase();
  const actionExists = actionArray.filter((item) => {
    return (item?.groupName?.toLocaleLowerCase() === lowerGroupName && item?.account?.title === accountName) ||
    item?.toGroupName?.toLocaleLowerCase() === lowerGroupName;
  });
  return (actionExists?.length > 0);
};

export const getActionLastIndex = (arr: GenericObject[], mode: string) => {
  return findLastIndex(arr, (item) => {
    return item?.dataFor === mode;
  });
};

export const getMemberList = (arr) => {
  return arr?.map(({id}) => id);
};

export const getAccountLink = (account) => {
  const accountLink: AccountLink = {...account};
  delete accountLink?.subAccounts;
  return accountLink;
};

/**
 * utility that takes devices or assets and Entity type and
 * returns entity that associated with any groups
 */
export const getMembers = (devices: Device[] | Asset[], module: Entity): GroupMembers => {
  const isAsset = module === 'asset';
  return devices?.filter(device => {
    const row = isAsset ? device.asset : device;
    return row?.groups?.length && (isAsset ? !row.devices?.length : row.asset === null);
  }).map(item => setRel(isAsset ? item.asset : item, module));
};

const isAction = (...types: GroupActions[]) => (action: GroupActions): boolean => types.includes(action);

export const isRemoveAction     = isAction(GroupActions.Remove);
export const isAddAction        = isAction(GroupActions.Add);
export const isAddNewAction     = isAction(GroupActions.AddNew);
export const isReplaceNewAction = isAction(GroupActions.ReplaceNew);
export const isReplaceAction    = isAction(GroupActions.Replace);
